import type { ApiResponse } from '@bower/shared';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { signOut } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { getClientTabId } from '../utils/clientTabId';
import { currentBowerClient } from '../utils/bowerClient';
import { API_BASE_URL, IS_DEV } from '../config/env';
import { auth } from '../config/firebase';
import { getWorkspaceId } from '../utils/workspaceState';
import { mayUseAccount } from '../utils/accountSwitch';
import { safeAnalytics, getPageCategory } from '../utils/analytics';
import { shouldSampleApiRequest } from '../utils/apiSampling';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import { BOWER_CLIENT_HEADER } from '@bower/shared/constants/client';
import { gateForErrorCode } from '@bower/shared/billing/gates';
import { toPathTemplate, toRouteTemplate } from '@bower/shared/utils/routeTemplate';
import { emitUpgradeGateBlocked } from './events/upgradeGateEvents';
import type { HttpClient } from './api/http-client.types';
export type { AiQuotaState } from './api/http-client.types';

// This file is the HTTP CORE only: auth/workspace headers, token refresh,
// retries, analytics, and the ApiResponse envelope. Endpoint functions live
// in the domain modules under `services/api/` (projects.api, artifacts.api,
// …) and take `apiService` as their `HttpClient` first argument:
//
//   import { getProjects } from './api/projects.api';
//   const res = await getProjects(apiService, params);
//
// Don't add per-endpoint delegation methods back onto ApiService — the old
// facade held ~130 of them and pinned this file to the 1000-line guard.

interface ConnectionInfo {
  effectiveType?: string;
}

function getNetworkEffectiveType(): string | undefined {
  const nav = navigator as Navigator & { connection?: ConnectionInfo };
  return nav.connection?.effectiveType;
}

/**
 * Attach the HTTP status to a backend error envelope on its way out of the
 * client.
 *
 * The envelope the backend sends carries `{ code, message }` and nothing else,
 * so the status (the thing the response itself was carrying) is lost the moment
 * we return `error.response.data`. That is fine while a `code` fully determines
 * the remedy, and wrong the moment it does not: the connector-import read
 * failures (`GRAPH_READ_FAILED`, `TOOL_EXECUTION_FAILED`) come back under one
 * code whether the connected account needs reauthorizing (409) or the vendor is
 * throttling / down (503), and telling the second story for the first failure is
 * what sent a user to check their network over a Microsoft Graph 401 (staging,
 * 2026-08-26).
 *
 * Purely additive: a copy with one extra optional field, so nothing that reads
 * `.success` / `.error.code` / `.error.message` changes. Left untouched when the
 * body is not envelope-shaped (an HTML error page from a proxy, say); inventing
 * an `error` block there would turn a malformed response into a plausible-
 * looking one.
 */
function withHttpStatus<T>(data: unknown, status: number | undefined): ApiResponse<T> {
  if (status === undefined || typeof data !== 'object' || data === null) {
    return data as ApiResponse<T>;
  }
  const envelope = data as ApiResponse<T>;
  if (typeof envelope.error !== 'object' || envelope.error === null) return envelope;
  return { ...envelope, error: { ...envelope.error, status } };
}

/**
 * Firebase errors from `getIdToken(true)` that mean the refresh token is
 * gone and no retry can bring it back. Firebase's own client signs out on
 * the first two (`isUserInvalidated` in @firebase/auth). The secure-token
 * endpoint reports a revoked token as TOKEN_EXPIRED and a deleted user as
 * USER_NOT_FOUND, both mapped to `auth/user-token-expired`; an unmapped
 * INVALID_REFRESH_TOKEN falls through to `auth/invalid-refresh-token`.
 * Everything else, `auth/network-request-failed` above all, is transient.
 */
const SESSION_LOST_REFRESH_CODES: ReadonlySet<string> = new Set([
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/invalid-user-token',
  'auth/user-not-found',
  'auth/invalid-refresh-token',
]);

function isSessionLost(refreshError: unknown): boolean {
  // No Firebase user in this tab: there is no session left to keep.
  if (refreshError instanceof Error && refreshError.message === 'NO_AUTHENTICATED_USER') {
    return true;
  }
  const code = (refreshError as { code?: unknown } | null)?.code;
  return typeof code === 'string' && SESSION_LOST_REFRESH_CODES.has(code);
}

class ApiService implements HttpClient {
  private client: AxiosInstance;
  private authToken: string | null = null;
  /**
   * Shared in-flight refresh promise. When a 401 triggers a token
   * refresh, every concurrent 401 awaits the same promise instead of
   * each spawning its own `getIdToken(true)` call — and, critically,
   * instead of being silently locked out of refresh (the prior
   * `isRefreshingToken` boolean caused requests 2..N in a burst to
   * give up and propagate 401 to TanStack Query, which never retried).
   * Cleared in a `finally` so a subsequent 401 (e.g. after the user's
   * session genuinely expires) can refresh again.
   */
  private refreshTokenPromise: Promise<string> | null = null;
  private readonly verificationRefusalsTracked = new Set<string>();

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private addRequestHeaders(config: any): void {
    // Preserve correlationId + startTime across retries. The 401 retry
    // re-enters this interceptor on the same `error.config` object —
    // without this guard each retry mints a fresh correlation id and
    // resets startTime, breaking GCP log join and double-counting the
    // `API_REQUEST` PostHog event for one logical user action.
    if (!config.metadata) {
      config.metadata = { correlationId: uuidv4(), startTime: Date.now() };
    }
    config.headers['x-correlation-id'] = config.metadata.correlationId;

    // Opaque per-tab id, so the backend log can tell one looping tab from
    // several normal ones. Diagnostic only — never gates a request.
    const tabId = getClientTabId();
    if (tabId) {
      config.headers['x-client-tab-id'] = tabId;
    }

    // Declare which client this is, so the server can resolve
    // `Artifact.captureSurface` and the audit source once per request instead
    // of every create call site remembering to put one in a body. A per-call
    // override still wins.
    if (!config.headers[BOWER_CLIENT_HEADER]) {
      config.headers[BOWER_CLIENT_HEADER] = currentBowerClient();
    }

    if (this.authToken) {
      config.headers.Authorization = `Bearer ${this.authToken}`;
    }

    // Read workspace ID from WorkspaceContext on every request — but never
    // clobber an explicit per-call x-workspace-id. The WorkOS MCP sign-in flow
    // (POST /auth/workos/complete) targets the *chosen* workspace, which can
    // differ from the active one; overwriting it produced a body/header
    // workspaceId mismatch → 400 and a failed grant.
    if (!config.headers['x-workspace-id']) {
      const workspaceId = getWorkspaceId();
      if (workspaceId) {
        config.headers['x-workspace-id'] = workspaceId;
      }
    }
  }

  private trackApiRequest(
    method: string,
    url: string,
    status: number,
    durationMs: number,
    correlationId: string | undefined,
  ): void {
    if (!shouldSampleApiRequest(status, durationMs)) return;

    // Defer to a microtask so analytics work never competes with the
    // promise chain unwinding the original request — important for the
    // error path, where axios's rejection must propagate unobstructed.
    // Swallow any failure inside the microtask (incl. partial mocks of
    // analytics.ts in tests) so it can't surface as an unhandled rejection.
    queueMicrotask(() => {
      try {
        safeAnalytics.track(ANALYTICS_EVENTS.API_REQUEST, {
          route_template: toRouteTemplate(method, url),
          status,
          duration_ms: durationMs,
          is_error: status >= 400,
          correlation_id: correlationId,
          page_category: getPageCategory(window.location.pathname),
          network_effective_type: getNetworkEffectiveType(),
        } satisfies Record<string, unknown>);
      } catch {
        // intentionally ignored — analytics must never break the app
      }
    });
  }

  private logApiCall(config: any, status: number, metadata: any): void {
    const duration = Date.now() - (metadata?.startTime || Date.now());
    const method = config.method?.toUpperCase() || 'UNKNOWN';
    const url = config.url || '';

    this.trackApiRequest(method, url, status, duration, metadata?.correlationId);

    if (IS_DEV) {
      console.info(`🌐 API ${method} ${url} - ${status} (${duration}ms)`, {
        correlationId: metadata?.correlationId,
        userId: this.getAuthToken() ? 'authenticated' : undefined,
        workspaceId: this.getWorkspaceId() || undefined,
      });
    }
  }

  private logApiError(error: any): void {
    const correlationId = error.config?.metadata?.correlationId;
    const duration = Date.now() - (error.config?.metadata?.startTime || Date.now());
    const status = error.response?.status || 0;
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const url = error.config?.url || '';

    this.trackApiRequest(method, url, status, duration, correlationId);

    console.error(`❌ API ${method} ${url} - ${status} (${duration}ms)`, {
      correlationId,
      userId: this.getAuthToken() ? 'authenticated' : undefined,
      workspaceId: this.getWorkspaceId() || undefined,
      errorCode: error.code,
      errorMessage: error.message,
    });
  }

  private redirectToAuth(): void {
    if (!window.location.pathname.includes('/auth')) {
      window.location.href = '/auth';
    }
  }

  /**
   * Force-refresh the Firebase ID token, deduplicated across concurrent
   * callers. The first 401 in a burst starts the refresh; every other
   * 401 awaits the same promise and replays with the new token. This
   * is the fix for the "thundering herd" 401 burst that left subsequent
   * queries permanently in an error state (incident 2026-05-16: page
   * loads after >1h idle hit 15-18 concurrent 401s; the prior
   * `isRefreshingToken` boolean only let the first one refresh and
   * silently dropped the rest, producing partial-data renders such as
   * a note that should be editable showing as read-only).
   *
   * We call `getIdToken(true)` deliberately — the server already
   * rejected the cached token as `auth/id-token-expired`, so a
   * non-forced refresh would risk returning the same stale token if
   * the SDK's clock disagrees with the server.
   */
  private refreshAuthToken(): Promise<string> {
    if (this.refreshTokenPromise) return this.refreshTokenPromise;
    const user = auth.currentUser;
    if (!user) {
      return Promise.reject(new Error('NO_AUTHENTICATED_USER'));
    }
    // Another tab signed in as someone else and this page has not gone yet:
    // never fetch their token here. Not a lost session, so the 401 surfaces.
    if (!mayUseAccount(user.uid)) {
      return Promise.reject(new Error('ACCOUNT_SWITCHED'));
    }
    this.refreshTokenPromise = (async () => {
      try {
        const fresh = await user.getIdToken(true);
        this.setAuthToken(fresh);
        return fresh;
      } finally {
        this.refreshTokenPromise = null;
      }
    })();
    return this.refreshTokenPromise;
  }

  private trackVerificationRefused(): void {
    const user = auth.currentUser;
    if (!user || !safeAnalytics.isReady() || this.verificationRefusalsTracked.has(user.uid)) return;
    this.verificationRefusalsTracked.add(user.uid);
    safeAnalytics.track(ANALYTICS_EVENTS.EMAIL_VERIFICATION_REFUSED, {
      has_sso_identity: user.providerData.some(({ providerId }) =>
        ['google.com', 'microsoft.com', 'apple.com'].includes(providerId),
      ),
    });
  }

  /**
   * Bounded one-shot retry for 403 EMAIL_VERIFICATION_REQUIRED. Forces a
   * Firebase reload + token refresh, reissues the original request with the
   * new token, and lets the response speak for itself. On any failure we
   * fall through to the original 403 — the caller still sees the error and
   * can surface its own UI; the worst case is we have not made things worse.
   *
   * The __verifRetried marker on the request config is checked by the
   * response interceptor to ensure a single attempt per request.
   */
  private async attemptVerificationRefresh(error: any): Promise<any> {
    const user = auth.currentUser;
    if (!user || !error.config || !mayUseAccount(user.uid)) {
      return Promise.reject(error);
    }

    try {
      await user.reload();
      const fresh = await user.getIdToken(true);
      this.setAuthToken(fresh);

      error.config.__verifRetried = true;
      error.config.headers = error.config.headers || {};
      error.config.headers.Authorization = `Bearer ${fresh}`;

      return await this.client.request(error.config);
    } catch {
      return Promise.reject(error);
    }
  }

  /**
   * One-shot retry on 401. The `__authRetried` marker on the request
   * config (set by the response interceptor) prevents a second pass
   * if the retry itself 401s — that would indicate either a deploy-
   * level token misconfig or genuinely revoked access, neither of
   * which an in-loop refresh can recover from.
   *
   * Only a refresh that proves the session is gone signs out. Firebase
   * syncs sign-out across tabs (config/firebase.ts), so a sign-out here
   * ends EVERY tab's session: a network blip during the refresh, or the
   * replayed request failing for any reason (5xx, 403, a second 401,
   * offline), surfaces the original 401 and leaves the session alone.
   */
  private async attemptTokenRefresh(error: any): Promise<any> {
    let newToken: string;
    try {
      newToken = await this.refreshAuthToken();
    } catch (refreshError) {
      if (!isSessionLost(refreshError)) {
        console.warn('Token refresh failed, keeping the session:', refreshError);
        return Promise.reject(error);
      }
      // Under a burst of concurrent 401s, N callers share one failed
      // refresh and reach this branch in parallel. Short-circuit on
      // `authToken === null` so only the first one runs the signOut +
      // redirect cycle; the rest just surface their original 401.
      // clearAuth is idempotent but signOut queues a Firebase
      // auth-state listener fire per call and redirectToAuth races a
      // navigation that may unmount the tree from under in-flight
      // awaiters.
      if (this.authToken === null) {
        return Promise.reject(error);
      }
      console.error('Token refresh failed, session lost:', refreshError);
      this.clearAuth();
      await signOut(auth);
      this.redirectToAuth();
      return Promise.reject(error);
    }

    error.config.headers = error.config.headers || {};
    error.config.headers.Authorization = `Bearer ${newToken}`;
    try {
      return await this.client.request(error.config);
    } catch {
      return Promise.reject(error);
    }
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      (config) => {
        this.addRequestHeaders(config);
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.client.interceptors.response.use(
      (response) => {
        this.logApiCall(response.config, response.status, (response.config as any).metadata);
        return response;
      },
      async (error) => {
        this.logApiError(error);

        // Track API errors for analytics — skip expected/noisy status codes:
        //  401: handled by token refresh
        //  403 with EMAIL_VERIFICATION_REQUIRED: normal during signup before email is verified
        //  403 with REAUTH_REQUIRED: normal sudo challenge; the caller reauths and retries
        //  404: often stale TanStack Query background refetches for deleted/missing resources
        const status = error.response?.status;
        const errorCode = error.response?.data?.error?.code;
        const isExpected =
          status === 401 ||
          (status === 403 &&
            (errorCode === 'EMAIL_VERIFICATION_REQUIRED' || errorCode === 'REAUTH_REQUIRED')) ||
          status === 404;
        if (status && !isExpected) {
          safeAnalytics.track(ANALYTICS_EVENTS.API_ERROR_OCCURRED, {
            status_code: status,
            // Templated like API_REQUEST: the raw URL carries ids and search terms.
            endpoint: toPathTemplate(error.config?.url ?? ''),
            method: error.config?.method?.toUpperCase() || 'UNKNOWN',
          } as any);
        }

        if (error.response?.status === 401) {
          // Pre-auth 401s (request fired before Firebase hydrated the token)
          // are a race, not a session loss. Reject quietly so TanStack Query
          // retries when auth becomes ready; don't redirect. This prevents
          // refreshes on protected pages from bouncing to /auth.
          const sentAuth = !!error.config?.headers?.Authorization;
          if (!sentAuth) {
            return Promise.reject(error);
          }

          // One retry per request. If the retry also 401s, something
          // unrecoverable is going on (revoked refresh token, project
          // misconfig); fall through to the existing analytics path
          // and let the caller surface the error.
          if (!error.config.__authRetried) {
            error.config.__authRetried = true;
            return this.attemptTokenRefresh(error);
          }
        }

        // Self-heal stale-token 403s. The user may have verified their email
        // in another tab / mobile webview / via the polling path on
        // EmailVerificationPrompt, in which case Firebase has a fresh token
        // with email_verified=true but the cached token in our request
        // header still carries the old claim. Force-refresh once and retry;
        // if the user is genuinely unverified, the retried request will
        // reject again and the __verifRetried marker prevents a loop.
        if (
          error.response?.status === 403 &&
          error.response?.data?.error?.code === 'EMAIL_VERIFICATION_REQUIRED' &&
          !(error.config as any)?.__verifRetried
        ) {
          return this.attemptVerificationRefresh(error);
        }
        if (
          error.response?.status === 403 &&
          error.response?.data?.error?.code === 'EMAIL_VERIFICATION_REQUIRED'
        ) {
          this.trackVerificationRefused();
        }

        // Handle 403 VIEWER role write attempts. Map both the Prisma
        // role-guard error (`VIEWER_FORBIDDEN`) and the upstream HTTP
        // middleware error (`AUTHORIZATION_ERROR` from `requireWriteAccess`)
        // to a single, consistent toast — instead of every mutation
        // surface inventing its own generic "Update failed" message.
        // The interceptor still rejects the promise so individual
        // callers' catch blocks fire; the toast just adds a clear
        // role-aware reason.
        if (error.response?.status === 403) {
          const code = error.response?.data?.error?.code;
          const message = error.response?.data?.error?.message;
          // The workspace is gone or scheduled for deletion: move the user
          // on (WorkspaceContext), rather than a read-only toast.
          if (code === 'WORKSPACE_UNAVAILABLE') {
            const header = error.config?.headers?.['x-workspace-id'];
            const { emitWorkspaceUnavailable } =
              await import('./events/workspaceUnavailableEvents');
            emitWorkspaceUnavailable(typeof header === 'string' ? header : null);
          }
          if (code === 'VIEWER_FORBIDDEN' || code === 'AUTHORIZATION_ERROR') {
            const { emitViewerForbidden } = await import('./events/viewerForbiddenEvents');
            emitViewerForbidden(message);
          }
        }

        // Handle 402 Payment Required — tier / feature gating.
        // Map the backend error code to a shared GateKey and emit an event;
        // UpgradePromptModal subscribes on mount and opens itself. This
        // keeps the HTTP service layer free of UI-store imports (so it
        // remains testable + SSR-safe). `gateForErrorCode` is a pure
        // shared helper enforcing FE/BE parity via a satisfies-typed map.
        //
        // Backend writes the gate context in two slightly different shapes:
        //   - requireFeature middleware: { feature, requiredTier, currentTier } at top level
        //   - FeatureRequiredError service guard: same fields under `details`
        // We normalise both into a single GateContext so the generic
        // `tier-feature` modal renders registry-driven copy on either path.
        if (error.response?.status === 402) {
          const errorData = error.response?.data?.error;
          const feature = errorData?.feature ?? errorData?.details?.feature;
          const requiredTier = errorData?.requiredTier ?? errorData?.details?.requiredTier;
          const currentTier = errorData?.currentTier ?? errorData?.details?.currentTier;

          const gateKey = errorData?.code ? gateForErrorCode(errorData.code) : null;
          safeAnalytics.track(ANALYTICS_EVENTS.BILLING_GATE_BLOCKED, {
            gate_key: gateKey ?? errorData?.code ?? '',
            required_tier: requiredTier ?? '',
            current_tier: currentTier ?? '',
            source: 'backend-402',
          } as Record<string, string>);
          if (gateKey) {
            emitUpgradeGateBlocked(gateKey, {
              ...(errorData?.details ?? {}),
              // Promote normalised fields into the context so the modal's
              // `tier-feature` body can resolve copy from FEATURE_REGISTRY
              // via featureKey regardless of which backend layer fired.
              featureKey: feature,
              requiredTier,
              currentTier,
            });
          }
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * A write that survives the document being torn down.
   *
   * Axios cannot do this: an XHR in flight is cancelled when the page unloads,
   * so a debounced save flushed on `pagehide` or `visibilitychange` starts and
   * then dies with the page. Measured against a real server - a protocol edit
   * typed and immediately hard-navigated away from never reached the database,
   * with the flush listener in place and firing.
   *
   * `fetch` with `keepalive` is the platform's answer, and it is deliberately
   * NOT a general escape hatch: it caps the body at 64KB, it cannot be
   * retried, and nothing can read its result, because by the time it settles
   * there is no page to tell. Use it only for a last write on the way out, and
   * keep the ordinary client for everything else.
   *
   * Headers are rebuilt here rather than reused, because the interceptor that
   * normally adds them runs inside axios and this request never enters it. Any
   * new header the interceptor starts adding has to be added here too - which
   * is the cost of the one path that bypasses it.
   */
  sendKeepalive(method: string, path: string, body: unknown): boolean {
    if (typeof fetch !== 'function') return false;

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.authToken) headers.Authorization = `Bearer ${this.authToken}`;
    const workspaceId = getWorkspaceId();
    if (workspaceId) headers['x-workspace-id'] = workspaceId;
    headers[BOWER_CLIENT_HEADER] = currentBowerClient();

    try {
      // No `await`: the caller is unloading and has nothing to wait with. The
      // `catch` is for a synchronous throw only; a rejected promise is swallowed
      // deliberately, because a failure here has no surface left to report on.
      void fetch(`${API_BASE_URL}${path}`, {
        method,
        headers,
        body: JSON.stringify(body),
        keepalive: true,
        credentials: 'include',
      }).catch(() => {});
      return true;
    } catch {
      return false;
    }
  }

  setAuthToken(token: string): void {
    this.authToken = token;
  }

  clearAuth(): void {
    this.authToken = null;
    this.refreshTokenPromise = null;
  }

  /**
   * Public hook for other subsystems (notably SocketContext) to share
   * the same dedup'd refresh path used by the response interceptor.
   * Concurrent callers — including a HTTP 401 cohort racing with a
   * socket `connect_error` — all await the same in-flight promise,
   * so Firebase sees exactly one `getIdToken(true)` per cohort.
   * Returns the fresh token on success; rejects on Firebase failure.
   */
  refreshAuth(): Promise<string> {
    return this.refreshAuthToken();
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  /**
   * Get current workspace ID from WorkspaceContext
   * This is a convenience method for external callers
   */
  getWorkspaceId(): string | null {
    return getWorkspaceId();
  }

  // Generic request method with retry logic
  async request<T>(config: AxiosRequestConfig, retries: number = 1): Promise<ApiResponse<T>> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await this.client.request(config);

        // Normalize 204 No Content: axios yields '' for the empty body, and
        // '' has no `.success`, so envelope consumers — unwrapApiResponse and
        // every `.success` branch — would misread a SUCCESSFUL response as a
        // failure (false "failed to dismiss" toasts on the 204 checklist
        // endpoints; PR #479 review). Deliberately 204-ONLY: an empty body on
        // any other 2xx has no legitimate producer (every non-204 backend 2xx
        // is a JSON envelope), so it is an anomaly — proxy stripping,
        // truncation, a misdeployed route — and must stay a loud failure,
        // not become a silent success (PR #483 review).
        if (response.status === 204) {
          return { success: true };
        }

        return response.data;
      } catch (error: any) {
        // If this is the last attempt, throw the error
        if (attempt === retries) {
          // Log PATCH errors with extra detail
          if (config.method?.toUpperCase() === 'PATCH') {
            console.error('❌ PATCH Error:', {
              url: config.url,
              attempt: attempt + 1,
              error: error.message,
              code: error.code,
              response: error.response?.data,
              status: error.response?.status,
            });
          }

          if (error.response?.data) {
            return withHttpStatus(error.response.data, error.response.status);
          }

          // Enhanced error categorization
          let errorCode = 'NETWORK_ERROR';
          let errorMessage = error.message || 'Network request failed';

          if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            errorCode = 'TIMEOUT';
            errorMessage = 'Request timed out. Please try again.';
          } else if (error.code === 'ECONNREFUSED') {
            errorCode = 'CONNECTION_REFUSED';
            errorMessage = 'Unable to connect to server. Please check if the server is running.';
          } else if (error.code === 'ENOTFOUND') {
            errorCode = 'DNS_ERROR';
            errorMessage = 'Server not found. Please check your internet connection.';
          } else if (!error.response) {
            errorCode = 'NETWORK_ERROR';
            errorMessage = 'Network error. Please check your internet connection.';
          }

          return {
            success: false,
            error: {
              code: errorCode,
              message: errorMessage,
            },
          };
        }

        // Only retry on network errors or 5xx server errors
        const shouldRetry =
          !error.response ||
          error.response.status >= 500 ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ECONNREFUSED';

        if (!shouldRetry) {
          // Don't retry client errors (4xx), just return them
          if (error.response?.data) {
            return withHttpStatus(error.response.data, error.response.status);
          }

          return {
            success: false,
            error: {
              code: 'CLIENT_ERROR',
              message: error.message || 'Request failed',
            },
          };
        }

        // Wait before retrying (exponential backoff)
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }

    // This should never be reached, but TypeScript requires it
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: 'An unknown error occurred',
      },
    };
  }

  // HTTP method helpers
  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    // Use retry logic for PATCH operations since they're critical data updates
    return this.request<T>({ ...config, method: 'PATCH', url, data }, 2);
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // File upload helper
  async uploadFile<T>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  /**
   * Get the axios client instance for direct access
   * Used for specialized requests like image fetching for export
   */
  getClient() {
    return this.client;
  }
}

// Create singleton instance
export const apiService = new ApiService();
